import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { useStatsigExperimentBucket } from '@src/experiments/statsig-integration/useStatsigExperiment'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getIsWhiteGloveDelivery,
  isProductAlcohol,
} from '@src/quince-site-common/src/helpers/productHelper'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { TrackConfig } from '../../hocs/withTracking'
import { default as Icon } from '../icon'
import { Image } from '../image'
import { default as Modal } from '../modal'

import { ICON_CONSTANTS, ICON_CONSTANTS_MAP } from './icon-constants'
import AgeRestricted from './icons/ageRestricted.inline.svg'
import Authenticity from './icons/authenticity.inline.svg'
import Delivery from './icons/delivery.inline.svg'
import DeliveryV2 from './icons/deliveryV2.inline.svg'
import Installments from './icons/installments.inline.svg'
import Lock from './icons/lock.inline.svg'
import Return from './icons/return.inline.svg'
import RightArrow from './icons/rightArrow.inline.svg'
import stylesV2 from './ProductDescFooterV2.module.scss'
import styles from './productForm.module.scss'
export const ProductDescFooter = ({
  hideFooter,
  isReturnable,
  product,
  selectedOptions,
  showNewPdpDesignV2,
  isFinalSaleProduct,
}: {
  hideFooter: boolean | undefined
  isReturnable: boolean | undefined
  product: Product | undefined
  selectedOptions?: ProductVariantOption[]
  showNewPdpDesignV2?: boolean
  isFinalSaleProduct?: boolean
}) => {
  const { t } = useTranslation('common')
  const [showEasyReturnModal, setShowEasyReturnModal] = useState(false)
  const secondChargingExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING)
  const isInChargingExpBucket = secondChargingExpBucket === 'Show'
  const isAgeRestrictedProduct = productHelper?.getIsAgeRestrictedProduct(product as Product)
  const ageLimitInYears = productHelper?.getCustomFieldValue(
    product as Product,
    CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS,
    false,
    true,
  )
  if (hideFooter) return <></>
  const isWhiteGloveDelivery = getIsWhiteGloveDelivery(product)

  const easyReturnsTrackConfig: TrackConfig = {
    trackGA: true,
    trackInternal: true,
    // @i18n-ignore
    category: 'checkout',
    // @i18n-ignore
    action: 'easy_returns',
    context: {
      product_id: product?.spid,
      title: product?.title,
      tags: product?.tags,
      subClassId: product?.subClassId,
      color: selectedOptions?.find(option => option.name === 'Color')?.value,
      size: selectedOptions?.find(option => option.name === 'Size')?.value,
    },
  }

  const handleEasyReturnClick = () => {
    setShowEasyReturnModal(prev => !prev)
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, easyReturnsTrackConfig)
  }

  const finalStyles = showNewPdpDesignV2 ? stylesV2 : styles
  const FreeShippingText = ({ isWhiteGloveDelivery }: { isWhiteGloveDelivery: boolean }) => {
    const { t } = useTranslation('common')
    return (
      <div
        className={classNames(finalStyles.container__info__main__text, {
          [finalStyles.container__whiteGloveTextWidth ?? '']: isWhiteGloveDelivery,
        })}
      >
        {isWhiteGloveDelivery ? (
          showNewPdpDesignV2 ? (
            t('free_white_glove_delivery')
          ) : (
            <>
              {t('free_white')}
              <br /> {t('glove_delivery')}
            </>
          )
        ) : showNewPdpDesignV2 ? (
          t('free_standard_shipping')
        ) : (
          t('free_shipping')
        )}
      </div>
    )
  }
  return (
    <div
      data-testid="product-desc-footer"
      data-hide-footer={hideFooter?.toString()}
      data-is-returnable={isReturnable?.toString()}
      className={classNames(finalStyles.container__info, finalStyles.container__info_newDesignV2)}
    >
      <div className={finalStyles.container__info__main}>
        <div className={finalStyles.container__info__main__icon}>
          <Icon
            iconSvg={showNewPdpDesignV2 ? DeliveryV2 : Delivery}
            className={finalStyles.container__info__main__icon__delivery}
          />
        </div>
        <FreeShippingText isWhiteGloveDelivery={!!isWhiteGloveDelivery} />
      </div>
      {isReturnable && !product?.hasRestockingFee ? (
        isFinalSaleProduct ? (
          <div className={finalStyles.container__info__main}>
            <div className={finalStyles.container__info__main__icon}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.INSTALLMENTS]}
                title={ICON_CONSTANTS.INSTALLMENTS}
                alt=""
                className={finalStyles.container__info__main__icon__installments}
              />
            </div>
            <div className={finalStyles.container__info__main__text}>{t('pay_in_installments')}</div>
          </div>
        ) : (
          <div className={finalStyles.container__info__main}>
            <div className={finalStyles.container__info__main__icon}>
              <Icon iconSvg={Return} className={finalStyles.container__info__main__icon__return} />
            </div>
            {isInChargingExpBucket ? (
              <div
                className={classNames(finalStyles.container__info__main__text, finalStyles.easyReturns)}
                onClick={handleEasyReturnClick}
              >
                <p>{showNewPdpDesignV2 ? t('easy_returns_v2') : t('easy_returns')}</p>
                {!showNewPdpDesignV2 && (
                  <span className={finalStyles.iconWrapper}>
                    <Icon className={finalStyles.icon} iconSvg={RightArrow} />
                  </span>
                )}
              </div>
            ) : (
              <div className={finalStyles.container__info__main__text}>
                {showNewPdpDesignV2 ? t('easy_returns_v2') : t('easy_returns')}
              </div>
            )}
          </div>
        )
      ) : (
        <div className={finalStyles.container__info__main}>
          {isAgeRestrictedProduct ? (
            showNewPdpDesignV2 ? (
              <div className={finalStyles.container__info__main__icon}>
                <AgeRestricted className={finalStyles.container__info__main__icon__installments} />
              </div>
            ) : (
              <div className={finalStyles.container__info__main__icon}>
                <Authenticity className={finalStyles.container__info__main__icon__installments} />
              </div>
            )
          ) : (
            <div className={finalStyles.container__info__main__icon}>
              <Installments className={finalStyles.container__info__main__icon__installments} />
            </div>
          )}

          <div
            className={classNames(finalStyles.container__info__main__text, {
              [finalStyles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
            })}
          >
            {isAgeRestrictedProduct ? (
              showNewPdpDesignV2 ? (
                ageLimitInYears ? (
                  t('age_restricted_product', { ageLimitInYears })
                ) : (
                  t('age_restricted_product_v2')
                )
              ) : (
                t('authenticity_guaranteed')
              )
            ) : showNewPdpDesignV2 ? (
              t('pay_in_installments')
            ) : (
              <>
                <span>{t('pay_in')}</span> {t('installments')}
              </>
            )}
          </div>
        </div>
      )}
      <div className={finalStyles.container__info__main}>
        <div className={finalStyles.container__info__main__icon}>
          <Icon iconSvg={Lock} className={finalStyles.container__info__main__icon__lock} />
        </div>
        <div
          className={classNames(finalStyles.container__info__main__text, {
            [finalStyles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
          })}
        >
          <span>{t('secure_payment')} </span>
        </div>
      </div>
      {!isReturnable && showNewPdpDesignV2 && isProductAlcohol(product) && (
        <div className={finalStyles.container__info__main}>
          <Image
            url={
              ICON_CONSTANTS_MAP[
                showNewPdpDesignV2 ? ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW : ICON_CONSTANTS.NON_RETURNABLE_BLACK
              ]
            }
            title={ICON_CONSTANTS.NON_RETURNABLE_BLACK}
            alt=""
            className={finalStyles.container__info__main__icon__return}
          />
          <div className={finalStyles.container__info__main__text}>{t('not_returnable_food_safety')}</div>
        </div>
      )}
      {showEasyReturnModal && isInChargingExpBucket && (
        <Modal
          open={showEasyReturnModal}
          className={finalStyles.easyReturnModal}
          modalWrapperClassName={finalStyles.easyReturnModalWrapper}
          showCloseButton={true}
          onClose={handleEasyReturnClick}
        >
          <div>
            <h3 className={finalStyles.modalHeader}>{t('return_policy')}</h3>
            <div className={finalStyles.subText}>{t('first_return_free_policy')}</div>
            <a
              className={finalStyles.redirectLink}
              // @i18n-ignore
              href={`${window.location.origin}/shipping-returns`}
            >
              {t('learn_more')}
            </a>
          </div>
        </Modal>
      )}
      {showNewPdpDesignV2 && !isReturnable && !isAgeRestrictedProduct && (
        <div className={finalStyles.container__info__main}>
          <div className={finalStyles.container__info__main__icon}>
            <Image
              url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW]}
              title={ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW}
              alt=""
              className={finalStyles.container__info__main__icon__return}
            />
          </div>
          <div className={finalStyles.container__info__main__text__noReturn}>{t('this_item_is_not_returnable')}</div>
        </div>
      )}
    </div>
  )
}
